import { graphQLClient } from '@api/client';
import { useTreatmentsQuery, TreatmentStatusEnum, Treatment } from '@api/GoodocAPI';
import {
  FocusableComponentLayout,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import {
  TREATMENT_HISTORY_EMPTY,
  TREATMENT_HISTORY_FIRST
} from '@pages/TreatmentHistory/focusKey';
import { generateHistoryDetailPath, ROOT } from '@router/path';
import constate from 'constate';
import { RefObject, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@context/Auth';
import { getRequestTreatment, getUnpaidTreatment } from '@utils/treatmentUtil';

const useTreatmentHistory = () => {
  const { isSignIn } = useAuthContext();
  const {
    isLoading,
    isFetching,
    data: treatmentsResponse,
    refetch: refetchTreatments
  } = useTreatmentsQuery(graphQLClient, undefined, { enabled: isSignIn });

  const [loaderVisible, setLoaderVisible] = useState(true);

  const setLoaderVisibility = useCallback(() => {
    setLoaderVisible(isFetching);
  }, [isFetching]);

  const completionTreatments = treatmentsResponse?.treatments.filter(
    (treatment) =>
      treatment.status === TreatmentStatusEnum.Completion ||
      treatment.status === TreatmentStatusEnum.Orderprescription
  );

  const unPaidTreatment =
    getUnpaidTreatment(treatmentsResponse?.treatments as Treatment[]) ?? undefined;

  const requestTreatment =
    getRequestTreatment(treatmentsResponse?.treatments as Treatment[]) ?? undefined;

  const { setFocus } = useFocusable();

  const setFirstRowFocus = useCallback(
    () => setFocus(TREATMENT_HISTORY_FIRST),
    [setFocus]
  );

  const setEmptyListFocus = useCallback(
    () => setFocus(TREATMENT_HISTORY_EMPTY),
    [setFocus]
  );

  const navigate = useNavigate();

  const goTreatmentHistoryDetail = useCallback(
    (treatmentId: string) => {
      navigate(generateHistoryDetailPath(treatmentId));
    },
    [navigate]
  );

  const goHome = () => {
    navigate(ROOT);
  };

  const scrollToItem = (
    ref: RefObject<HTMLDivElement>,
    { y }: FocusableComponentLayout
  ) => {
    if (ref.current) {
      ref.current.style.transform = `translateY(${-y}px)`;
    }
  };

  return {
    loaderVisible,
    setLoaderVisibility,
    completionTreatments,
    unPaidTreatment,
    requestTreatment,
    refreshTreatmentHistory: refetchTreatments,
    isLoading,
    goTreatmentHistoryDetail,
    goHome,
    setFirstRowFocus,
    scrollToItem,
    setEmptyListFocus
  };
};

export const [TreatmentHistoryProvider, useTreatmentHistoryContext] =
  constate(useTreatmentHistory);
